import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { Mail } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { AnalyticsKPIRow, OverviewKpis } from '@/components/analytics/AnalyticsKPIRow'
import {
  DataTrustPanel,
  DataTrustData,
} from '@/components/analytics/DataTrustPanel'
import { MarketActivityPanel } from '@/components/analytics/MarketActivityPanel'
import { KPICardSkeleton } from '@/components/shared/KPICard'
import { ChartChrome } from '@/components/shared/ChartChrome'
import { AnalyticsErrorBlock } from '@/components/shared/AnalyticsStates'
import { ExportButton } from '@/components/shared/ExportButton'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'

interface OverviewResponse {
  period: string
  periodLabel: string
  currentStart: string
  currentEnd: string
  previousStart: string
  previousEnd: string
  kpis: OverviewKpis
  heroSeries: Array<{
    weekStart: string
    listings: number
    sales: number
    priceChanges: number
  }>
  dataTrust: DataTrustData
  meta: {
    outlierTrimHardCap: { min: number; max: number }
    historySince: string
    historyWarning: string
  }
}

function OverviewBody() {
  const [sendingDigest, setSendingDigest] = useState(false)
  const { isAdmin } = useAuth()
  const { queryParams } = useAnalyticsFilters()
  const { data, isLoading, isError, error, refetch } = useAnalyticsQuery<OverviewResponse>(
    'overview',
    '/analytics/overview',
  )

  const handleSendDigest = async () => {
    setSendingDigest(true)
    try {
      // Scope the digest by the currently active filter context (cities /
      // typologies / districts / segment) but force a weekly window — the
      // digest is always "what happened this week". The scheduled Cloud
      // Scheduler job posts an empty body and gets the global view.
      const { period: _period, ...scope } = queryParams
      const filters = { period: '7d', ...scope }
      const res = await api.post('/analytics/email/send-digest', { filters })
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Digest sent')
      } else {
        toast.error(res.data?.message ?? 'Digest failed to send')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Digest failed to send')
    } finally {
      setSendingDigest(false)
    }
  }

  if (isError) {
    return (
      <div className="card">
        <AnalyticsErrorBlock
          title="Failed to load analytics overview"
          error={error}
          onRetry={() => {
            void refetch()
          }}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-500">
          {data ? data.periodLabel : '\u00a0'}
        </p>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={handleSendDigest}
              disabled={sendingDigest}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Send the weekly analytics digest now (admin only)"
            >
              <Mail className="w-4 h-4" />
              {sendingDigest ? 'Sending…' : 'Send digest now'}
            </button>
          )}
          <ExportButton
            endpoint="/analytics/export/overview.csv"
            params={queryParams}
            filename="analytics-overview"
          />
        </div>
      </div>

      {isLoading || !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <KPICardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <AnalyticsKPIRow
          kpis={data.kpis}
          periodLabel={data.periodLabel}
        />
      )}

      {data && (
        <ChartChrome
          title="Weekly activity (26 weeks)"
          subtitle={`Listings, sales and price changes. Data since ${data.meta.historySince}.`}
          info={{
            data: {
              definition:
                'Trailing-26-week view of the three main market signals: new listings (supply injection), closed sales (demand realized) and price changes (pricing pressure). Stacked so the overall bar height reads as total activity.',
              formula:
                'listings = COUNT(unit) by date_trunc(week, listing_date) · sales = COUNT(unit) by date_trunc(week, sales_date) · price_changes = COUNT(unit_history) where field = current_price',
              calculation:
                'Weeks are Monday-aligned. Price history starts on the date shown in the subtitle (scraper first began capturing unit_history rows). Events are not filter-scoped — this chart shows the full market pulse.',
              source: 'unit.listing_date, unit.sales_date, unit_history',
            },
          }}
          render={(height) => (
            <ResponsiveContainer width="100%" height={height}>
              <AreaChart
                data={data.heroSeries}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="gListings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gPrices" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="weekStart" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="listings"
                  name="Listings"
                  stroke="#3b82f6"
                  fill="url(#gListings)"
                  stackId="1"
                />
                <Area
                  type="monotone"
                  dataKey="sales"
                  name="Sales"
                  stroke="#16a34a"
                  fill="url(#gSales)"
                  stackId="1"
                />
                <Area
                  type="monotone"
                  dataKey="priceChanges"
                  name="Price changes"
                  stroke="#f59e0b"
                  fill="url(#gPrices)"
                  stackId="1"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        />
      )}

      <MarketActivityPanel />

      <DataTrustPanel data={data?.dataTrust} loading={isLoading} />
    </div>
  )
}

export function AnalyticsOverview() {
  return (
    <AnalyticsLayout>
      <OverviewBody />
    </AnalyticsLayout>
  )
}
